import { useNavigate } from 'react-router-dom';
import OfficialDiaryAppService from '../../../../application/platform/sdp/official-diary/official-diary-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { usePagedList } from '../../../../hooks/use-paged-list';
import { IOfficialDiaryPagedResponse } from '../../../../model/sdp/official-daily/response/official-diary-paged-response';
import { parseNumberInput } from '../../../../utils/format';
import { ACTION_TYPES, getDefaultOfficialDiaryParameters, useOfficialDiarySearchDispatch, useOfficialDiarySearchState } from "./official-diary-search-context";

const editionLabel = (row: IOfficialDiaryPagedResponse) => `${row.editionNumber}/${row.yearPublication}`;

export const useOfficialDiarySearchController = () => {
    const navigate = useNavigate();
    const dispatch = useOfficialDiarySearchDispatch();
    const { parameters, searchTrigger } = useOfficialDiarySearchState();
    const { showSuccess, showError, confirmAction, withLoading } = useFeedback();
    const list = usePagedList(OfficialDiaryAppService.get, parameters, searchTrigger, 'Não foi possível carregar as edições do Diário Oficial');

    function updateParameters(payload: object) {
        dispatch({ type: ACTION_TYPES.updateParameter, payload });
    }

    // Filtros apenas atualizam os parâmetros; a consulta ocorre em Consultar/Enter
    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'input-number') updateParameters({ number: parseNumberInput(value) });
        else if (name === 'input-year') updateParameters({ year: parseNumberInput(value) });
        else if (name === 'select-closed') updateParameters({ isClosed: value === '' ? undefined : value === 'true' });
        else if (name === 'select-client') updateParameters({ clientId: parseNumberInput(value) });
    }

    function handleStartDateChange(value: string | null) {
        updateParameters({ startDate: value ?? undefined });
    }

    function handleEndDateChange(value: string | null) {
        updateParameters({ endDate: value ?? undefined });
    }

    function handleSearch() {
        updateParameters({ page: 0 });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function handleClearFilters() {
        updateParameters({ ...getDefaultOfficialDiaryParameters(), pageSize: parameters.pageSize });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function handlePaginationChange(page: number, pageSize: number) {
        updateParameters({ page, pageSize });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function runAndReload(work: () => Promise<void>, successMessage: string, errorMessage: string) {
        return withLoading(async () => {
            try {
                await work();
                showSuccess(successMessage);
                await list.reload();
            } catch (error: any) {
                showError(errorMessage, error);
            }
        });
    }

    function handleChangeVisibility(row: IOfficialDiaryPagedResponse) {
        const target = row.isVisibility ? 'invisível' : 'visível';
        confirmAction('Alterar visibilidade', `Deseja realmente tornar a edição ${editionLabel(row)} ${target}?`, () =>
            runAndReload(() => OfficialDiaryAppService.changeVisibility(row.id), `A edição ${editionLabel(row)} agora está ${target}.`, 'Não foi possível alterar a visibilidade da edição')
        );
    }

    function handleCloseEdition(row: IOfficialDiaryPagedResponse) {
        confirmAction('Fechar edição', `Deseja realmente fechar a edição ${editionLabel(row)}?`, () =>
            runAndReload(() => OfficialDiaryAppService.close(row.id), 'Edição fechada com sucesso!', 'Não foi possível fechar a edição')
        );
    }

    function navigateToCreate() {
        navigate('/diario-oficial/publicar');
    }

    function navigateToDetail(row: IOfficialDiaryPagedResponse) {
        navigate(`/diario-oficial/detalhes/${row.id}`);
    }

    return {
        list,
        handleOnChange,
        handleStartDateChange,
        handleEndDateChange,
        handleSearch,
        handleClearFilters,
        handlePaginationChange,
        handleChangeVisibility,
        handleCloseEdition,
        navigateToCreate,
        navigateToDetail
    };
}
